import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';

import './styles.scss';

interface Props {
    activeButton: ReactNode;
    children: ReactNode;
    className?: string;
    wrapperClassName?: string;
    align?: 'left' | 'center' | 'right';
    verticalAlign?: 'top' | 'middle' | 'bottom';
    hideCloseIcon?: boolean;
    forceClose?: boolean;
    closeTooltipCallback?: () => void;
}

export const ClickTooltip = ({
    activeButton,
    children,
    className = '',
    align = 'center',
    verticalAlign = 'top',
    hideCloseIcon = false,
    wrapperClassName = '',
    forceClose = false,
    closeTooltipCallback,
}: Props) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const tooltipButton = useRef<HTMLDivElement>(null);
    const [tooltipVisible, setTooltipVisible] = useState(false);

    useEffect(() => {
        if (tooltipRef && tooltipRef.current && tooltipButton && tooltipButton.current) {
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const tooltipHeight = tooltipRef.current.offsetHeight;
            const tooltipButtonWidth = tooltipButton.current.offsetWidth;
            const tooltipButtonHeight = tooltipButton.current.offsetHeight;

            switch (align) {
                case 'left':
                    if (verticalAlign === 'top') {
                        tooltipRef.current.style.left = '0px';
                        tooltipRef.current.style.bottom = `calc(${tooltipButtonHeight}px + 4px)`;
                    }
                    if (verticalAlign === 'middle') {
                        tooltipRef.current.style.top = `calc(-1 * ((${tooltipHeight}px - ${tooltipButtonHeight}px) / 2))`;
                        tooltipRef.current.style.left = `calc(-1 * (${tooltipWidth}px + 8px))`;
                    }
                    if (verticalAlign === 'bottom') {
                        tooltipRef.current.style.left = '0px';
                        tooltipRef.current.style.bottom = '-4px';
                    }
                    break;
                case 'right':
                    if (verticalAlign === 'top') {
                        tooltipRef.current.style.right = '0px';
                        tooltipRef.current.style.bottom = `calc(${tooltipButtonHeight}px + 4px)`;
                    }
                    if (verticalAlign === 'middle') {
                        tooltipRef.current.style.top = `calc(-1 * ((${tooltipHeight}px - ${tooltipButtonHeight}px) / 2))`;
                        tooltipRef.current.style.right = `calc(-1 * (${tooltipWidth}px + 8px))`;
                    }
                    if (verticalAlign === 'bottom') {
                        tooltipRef.current.style.right = '0px';
                        tooltipRef.current.style.bottom = '-4px';
                    }

                    break;
                case 'center':
                    tooltipRef.current.style.left = `calc(-1 * ((${tooltipWidth}px - ${tooltipButtonWidth}px) / 2))`;
                    if (verticalAlign === 'top') {
                        tooltipRef.current.style.bottom = `calc(${tooltipButtonHeight}px + 4px)`;
                    }
                    if (verticalAlign === 'bottom') {
                        tooltipRef.current.style.bottom = '-4px';
                    }
                    break;
                default:
                    break;
            }
        }
    }, [tooltipRef, tooltipButton, align, verticalAlign]);

    useEffect(() => {
        if (forceClose) {
            closeTooltipHandler();
        }
    }, [forceClose]);

    const openTooltipHandler = () => {
        setTooltipVisible(true);
    };

    const closeTooltipHandler = () => {
        if (closeTooltipCallback) {
            closeTooltipCallback();
        }
        setTooltipVisible(false);
    };

    return (
        <div className={`app-click-tooltip ${wrapperClassName}`}>
            <div className='app-click-tooltip__activate-button' ref={tooltipButton} onClick={openTooltipHandler}>
                {activeButton}
            </div>

            {tooltipVisible && <div className='app-click-tooltip__overlay' onClick={closeTooltipHandler}></div>}

            <div
                className={`app-click-tooltip__popup-container ${
                    tooltipVisible ? 'app-click-tooltip__popup-container--visible' : ''
                } ${hideCloseIcon ? 'app-click-tooltip__popup-container--equal-padding' : ''} ${className} `}
                ref={tooltipRef}
            >
                {children}
                {!hideCloseIcon && (
                    <div className='app-click-tooltip__close' onClick={closeTooltipHandler}>
                        <Icon icon='xmark-regular' size='14px' className='app-click-tooltip__close-icon' />
                    </div>
                )}
            </div>
        </div>
    );
};
